/**
 * Public bundle preview — Phase 7 (Playwright bundle migration).
 *
 * Mounted at `/bundle-preview/:tripId` OUTSIDE the protected <Layout> in
 * App.js, so the CRM navbar / sidebar do NOT render into the Playwright-
 * generated PDF. Renders every voucher belonging to the trip stacked
 * vertically with a hard page-break between each voucher.
 *
 * Auth: hits the protected `/api/vouchers?trip_id={tripId}` endpoint.
 * The generatePDF() util forwards httpOnly cookies + the localStorage
 * Bearer token to `/api/pdf/render`, which the headless Chromium
 * replays before navigating here — same pattern proven by the voucher
 * and itinerary migrations.
 *
 * This route is intentionally NOT linked from any nav/menu. It exists
 * solely for the Playwright PDF render of a voucher bundle.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BrandedVoucherLayout from '@/components/voucher/BrandedVoucherLayout';
import FlightVoucherBody from '@/components/voucher/FlightVoucherBody';
import HotelVoucherBody from '@/components/voucher/HotelVoucherBody';
import {
  ActivityVoucherBody,
  TransferVoucherBody,
  ItineraryVoucherBody,
} from '@/components/voucher/VoucherBodies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BundleVoucherPage = ({ voucher, company, isFirst }) => {
  const qrValue =
    voucher.publicUrl ||
    `${window.location.origin}/voucher/${voucher.voucherId}`;
  return (
    <div
      style={{
        pageBreakBefore: isFirst ? 'auto' : 'always',
        breakBefore: isFirst ? 'auto' : 'page',
        // pageBreakInside: 'avoid' removed — caused Chromium to clip multi-page
        // voucher content (incl. prominent barcode/QR images on pages 2-3) when
        // forced onto a single A4 sheet. Each voucher SHOULD span multiple pages
        // naturally; the page-break-before above guarantees a fresh page start.
      }}
      data-testid={`bundle-voucher-${voucher.voucherId}`}
    >
      <BrandedVoucherLayout
        voucher={voucher}
        company={company}
        qrValue={qrValue}
        forPdf
      >
        {voucher.type === 'flight' && (
          <FlightVoucherBody voucher={voucher} company={company} />
        )}
        {voucher.type === 'hotel' && (
          <HotelVoucherBody voucher={voucher} company={company} />
        )}
        {voucher.type === 'activity' && (
          <ActivityVoucherBody voucher={voucher} company={company} />
        )}
        {voucher.type === 'transfer' && (
          <TransferVoucherBody voucher={voucher} company={company} />
        )}
        {voucher.type === 'itinerary' && (
          <ItineraryVoucherBody voucher={voucher} company={company} />
        )}
      </BrandedVoucherLayout>
    </div>
  );
};

const PublicBundle = () => {
  const { tripId } = useParams();
  const [vouchers, setVouchers] = useState([]);
  const [trip, setTrip] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, cRes, tRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/vouchers?trip_id=${tripId}`),
          axios.get(`${BACKEND_URL}/api/public/company`).catch(() => ({ data: {} })),
          axios
            .get(`${BACKEND_URL}/api/trips/${tripId}`)
            .catch(() => ({ data: null })),
        ]);
        setVouchers(Array.isArray(vRes.data) ? vRes.data : []);
        setCompany(cRes.data);
        setTrip(tRes.data);
      } catch (err) {
        setError('Bundle not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tripId]);

  // Set document.title so Chromium's PDF header placeholder picks up the
  // trip ref on every page automatically (same trick as PublicVoucher).
  useEffect(() => {
    if (trip || tripId) {
      document.title = trip?.tripId || tripId || 'Vouchers';
    }
  }, [trip, tripId]);

  // Opt this public/Playwright route out of the global @media print
  // visibility:hidden rule in index.css. Without this, page.pdf() blanks
  // the body and only the Playwright header/footer template survives.
  useEffect(() => {
    document.body.classList.add('pdf-public-route');
    return () => document.body.classList.remove('pdf-public-route');
  }, []);

  if (loading)
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  if (error || vouchers.length === 0)
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">
          {error || 'No vouchers found for this trip'}
        </p>
      </div>
    );

  return (
    <div
      className="min-h-screen bg-slate-100 py-8 px-4"
      data-testid="public-bundle-page"
    >
      <div className="max-w-5xl mx-auto">
        {vouchers.map((v, idx) => (
          <div
            key={v.voucherId}
            className="shadow-2xl mx-auto bg-white mb-8 last:mb-0"
          >
            <BundleVoucherPage
              voucher={v}
              company={company}
              isFirst={idx === 0}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PublicBundle;
